import React, { createContext, useState, useEffect } from "react";
import { authService } from "../services/authService";
import useToken from "../hooks/auth/useToken";
import { useNavigate } from "react-router-dom";
import { User } from "../utils/database";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: Error | null;
  refreshUser: () => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => Promise<void>;
  setUser: (user: User | null) => void; // Ajout de setUser
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  error: null,
  refreshUser: async () => {},
  logout: () => {},
  updateUser: async () => {},
  setUser: () => {} // Ajout de setUser
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const navigate = useNavigate()
  const {token , deleteToken} = useToken();

  
  // Récupérer l’user au montage
  useEffect(() => {
    let active = true;
    const fetchUser = async () => {
      if (!token) { setUser(null); setLoading(false); return; }
      setLoading(true);
      try {
        const currentUser = await authService.getUserProfile(token);
        if (active) { setUser(currentUser); setError(null); }
      } catch (err) {
        if (active) { setError(err as Error); setUser(null); }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchUser();
    return () => { active = false; };
  }, [token]);

  // Permet de forcer une mise à jour manuelle de l'user (ex: après login)
  const refreshUser = async () => {
    setLoading(true);
    try {
      const currentUser = await authService.getUserProfile(token);
      setUser(currentUser);
      setError(null);
    } catch (err) {
      setError(err as Error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    deleteToken()
    setUser(null);
    navigate("/login")
  };

  const updateUser = async (user: User) => {
    const {profile , avatar , username } = user
    try {
       await authService.updateUser(token , user.id! , { avatar , username} , profile)
    } catch (error) {
      throw error
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, error, refreshUser, logout, updateUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};